import _ from "lodash";
import {
  getQuestionAnswers, getQuestionType,
  getStepType,
  getUID,
  isNewDesignProcess, isRequiredQuestion,
  noAnswer
} from "../../utils";
import {CONFIRMATION_STATE, QUESTION_TYPE, STEP_TYPE} from "../../constants";

export class MenuItem {
  constructor(uid, name, getPrevStepAnswer) {
    this._uid = uid;
    this._name = name;
    this._step = null;
    this._isFirstStep = false;
    this._isLastStep = false;
    this._progress = 0;
    this._is_finished = false;
    this._questionnaire = undefined;
    this._getPrevStepAnswer = getPrevStepAnswer;
    this.__isCustomAnswers = true;
    this.__is_new_design = undefined;
    this.__is_with_new_other_assets = undefined;  // TODO: temporal switcher
  }

  get name() {
    return this._name;
  }

  get uid() {
    return this._uid;
  }

  get finished() {
    return this._is_finished;
  }

  set finished(value) {
    this._is_finished = value;
  }

  get step() {
    return this._step;
  }

  get progress() {
    return this._progress;
  }

  get currentStepNumber() {
    return this._curr_step_index;
  }

  get steps(){
    return this._questionnaire && this._questionnaire.steps || [];
  }

  get stepsLength() {
    return this.steps.length;
  }

  get isFistStep() {
    return this._isFirstStep;
  }

  get isLastStep() {
    return this._isLastStep;
  }

  get isDone(){
    // we assume that last step is congrats and not a question
    return (this._isLastStep && this.step && getStepType(this.step) === STEP_TYPE.CONGRATS) || this._is_finished;
  }

  get isNewDesign() {
    return this.__is_new_design;
  }

  get isWithNewOtherAssets() {
    return this.__is_with_new_other_assets;
  }

  getHasMissingAnswers(){
    return !!this._questionnaire && this._questionnaire.steps.some(
      step => !!step.confirmationState && this.checkHasMissingAnswers(step)
    )
  }

  checkHasMissingAnswers = (q) => {
    if(q.requiredMissing) {
      return q.requiredMissing()
    }

    return _.isArray(q.question) ? q.question.some(this.checkHasMissingAnswers) : this._checkRequiredQuestionMissing(q)
  };

  getStepsValidationData() {
    const result = [];

    this.steps.forEach(step => {
      if(step.confirmationState === CONFIRMATION_STATE.SKIPPED) {
        // Skipped steps can have both: prefilled answers to confirm AND required questions with missing answers.
        const questions = this._classifyQuestions(step);

        if (questions.length) {
          result.push({
            stepName: step.name,
            name: step.category,
            uid: step.uid,
            parent: step.parent,
            questions: questions,
          });
        }
      } else {
        const questionsWithMissingAnswers = this._getQuestionsWithMissingAnswers(step);

        if(!_.isEmpty(questionsWithMissingAnswers)) {
          result.push({
            stepName: step.name,
            name: step.category,
            uid: step.uid,
            parent: step.parent,
            questions: questionsWithMissingAnswers,
          });
        }
      }
    });

    return result;
  }

  /**
   * Single-pass recursive traversal that classifies leaf questions:
   * - has answer & not hidden → toConfirm: true (needs confirmation)
   * - required & missing answer → toConfirm: false (needs filling)
   */
  _classifyQuestions = (q, result = []) => {
    if (q._ignore || getQuestionType(q) === QUESTION_TYPE.HIDDEN) return result;

    if(q.requiredMissing) {
      result.push({question_text: q.question_text, name: q.name, toConfirm: !q.requiredMissing()});

      return result;
    }

    if (_.isArray(q.question)) {
      for (let i = 0; i < q.question.length; i++) {
        this._classifyQuestions(q.question[i], result);
      }
      return result;
    }

    result.push({question_text: q.question_text, name: q.name, toConfirm: !this._checkRequiredQuestionMissing(q)});

    return result;
  };

  _getQuestionsWithMissingAnswers = (q) => {
    if(_.isArray(q.question)){
      return _.flatten(q.question.map(question => this._getQuestionsWithMissingAnswers(question)).filter(Boolean));
    } else if(this._checkRequiredQuestionMissing(q)){
      return [q];
    }
  };

  _checkRequiredQuestionMissing = (q) => (q.required_check || isRequiredQuestion(q)) && noAnswer(q.answer, getQuestionType(q));

  hasSkippedSteps() {
    return this.steps.some(step => step.confirmationState === CONFIRMATION_STATE.SKIPPED)
  }

  async skipStep(stepToSkip) {

  }

  invalidateStaleSteps(answers) {
    if(_.isEmpty(answers)) return;

    this.steps.forEach(step => {
      if (step.confirmationState === CONFIRMATION_STATE.CONFIRMED && step.dependsOn) {
        for (const dep of step.dependsOn) {
          const depTimestamp = _.get(answers, [dep.menu, 'timestamps', dep.step]);
          const timestamp = _.get(answers, [this.uid, 'timestamps', this._getStepUID(step.uid)]);
          if (depTimestamp && timestamp && depTimestamp > timestamp) {
            step.confirmationState = CONFIRMATION_STATE.SKIPPED;
            break;
          }
        };
      }
    })
  }

  async initQuestionnaire(defaultAnswers, initOnly, lastFinishedStep) {
    await this.__setQuestionnaire(defaultAnswers, initOnly);

    if(!!initOnly) {
      const lastStepIdx = this._questionnaire && this._questionnaire.steps && this._questionnaire.steps.length - 1 || 0;
      this.__setStep(null, lastStepIdx);
      this._is_finished = true;
      return;
    } else {
      this._is_finished = false;

      if(this.__isCustomAnswers && !this.__keepStep){
        // clear prev data - to start from the beginning
        this._step = null;
        // Note: pass it ONLY when init (restore) progress and ignore for Interact
        if(!!lastFinishedStep){
          const steps = this._questionnaire && this._questionnaire.steps || [];
          const lastStepIdx = steps.findIndex(item => item.uid === lastFinishedStep);
          if(lastStepIdx !== -1){
            this._step = steps[lastStepIdx + 1] || steps[lastStepIdx];
          }
        }
      }
    }

    await this.sendCurrentStep();

    if (this._step){
      this.__setStep(0)
    } else {
      this.__setStep(null, 0)
    }
  }

  async sendCurrentStep(lastFinishedStepUID){
    // set current step in backend - to store progress
  }

  async __setQuestionnaire(defaultAnswers, initOnly){
    // in case resendResult keep prev flag
    if(defaultAnswers) {
      this.__is_new_design = isNewDesignProcess({onboarding_answers: defaultAnswers});
      if(!this.__is_new_design) {
        this.__is_with_new_other_assets = _.has(defaultAnswers, 'new_other_assets_dummy_step')
      }
    }
  }

  async __sendStepData() {

  }

  _afterStepChange(){}

  async nextStep() {
    const latestOnboardingData = await this.__sendStepData();
    await this._advanceStep();
    return latestOnboardingData
  }

  async _advanceStep() {
    if(this.isLastStep) {
      this._is_finished = true;
    } else {
      this.__setStep(1);
    }
  }

  async prevStep() {
    this.__setStep(-1);
  }

  async goToStep(step_uid) {
    const steps = this.steps;
    let latestOnboardingData;

    if(steps) {
      let targetIndex = steps.findIndex(s => s.uid === step_uid || this._getStepUID(s.uid) === step_uid);

      if(targetIndex !== -1) {
        const currentIndex = this.currentStepNumber - 1;
        // For forward jumps, skip intermediate steps
        for (let i = currentIndex; i < targetIndex; i++) {
          try {
            latestOnboardingData = await this.skipStep(steps[i]);
          } catch (err){
            if (err instanceof StepValidationError) {
              targetIndex = i;
              break;
            } else {
              throw err;
            }
          }
        }

        this.__setStep(null, targetIndex);
      }
    }

    return latestOnboardingData;
  }

  __setStep(direction, index) {
    if(this._questionnaire && this._questionnaire.steps) {
      const stepsLength = this._questionnaire.steps.length;
      const currentIndex = this._step && this._questionnaire.steps.findIndex(item => item.uid === this._step.uid) || 0;
      const requestedIndex = typeof index == 'number' ? index : currentIndex + direction;

      if (this._questionnaire.steps[requestedIndex]) {
        this._isFirstStep = requestedIndex === 0;
        this._isLastStep = requestedIndex === (stepsLength - 1);
        this._step = this._questionnaire.steps[requestedIndex];
        this._progress = Math.round(requestedIndex / (stepsLength - 1) * 10000) / 100;
        this._curr_step_index = requestedIndex + 1;

        this._afterStepChange();
      }
    }
  }

  _getStepUID = (uid) => {
    return this.__isCustomAnswers ? uid : getUID(uid);
  };

  _getQuestionUID = (uid) => {
    if (!this.__isCustomAnswers) {
      if (!['final_question_uid'].includes(uid)) {
        uid = getUID(uid);
      }
    }
    return uid;
  };

  getStep(stepUID) {
    stepUID = this._getStepUID(stepUID);
    return this._questionnaire && this._questionnaire.steps.find(step => this._getStepUID(step.uid) === stepUID)
  }

  findQuestionDeep(q, questionUID) {
    for(let question_index=0; question_index < (q.question || []).length; question_index++){
      let result;
      let nested_question = q.question[question_index];
      if(this._getQuestionUID(nested_question.uid) === questionUID){
        result = nested_question;
      } else if (!_.isEmpty(nested_question.question)){
        result = this.findQuestionDeep(nested_question, questionUID)
      }

      if (!_.isNil(result)) return result
    }
  }

  getQuestion(stepUID, questionUID){
    questionUID = this._getQuestionUID(questionUID);

    const step = this.getStep(stepUID);
    return step && this.findQuestionDeep(step, questionUID);
  }

  getStepAnswer(stepUID, questionUID, withOption, asQuestion=false){
    const question = this.getQuestion(stepUID, questionUID);
    if(question) {
      if (asQuestion) return question
      const answerData = getQuestionAnswers(question, withOption);
      if (answerData && answerData.answer) {
        return answerData.answer[0]
      } else {
        if (Array.isArray(answerData)) {
          return answerData
        }
      }
    }
  }

  __deepCopy = (data) => {
    return _.cloneDeep(data);
  };
}

export class StepValidationError extends Error {
  constructor(stepUID) {
    super();

    this.stepUID = stepUID
  }
}